import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { SimpleTabs } from "../../components/simplePage/simpleTabs.tsx";
import { SimpleFieldset } from "../../components/simplePage/simpleFieldset.tsx";
import { servicePost } from "../../api/client.ts";
import { useAuth } from "../../auth/authContext.tsx";
import { FwRichTextarea } from "../../components/ui/fwRichTextarea.tsx";
import styles from "../../styles/formPage.module.scss";
import type { ProductEditorHandle, ProductEditorProps } from "./editProductShared.ts";

/* ── Types ──────────────────────────────────────────────── */

interface CSProduct {
  ID: number;
  OperatorID: number;
  UniqueName: string;
  DisplayName: string;
  Description: string;
  StreetDate: string;
  Barcode: string;
  Depth: number;
  DeliverPrio: number;
  SingleDeliver: boolean;
  DeliverSpeed: number;
  Type: number;
  PriceType: number;
  DefaultPrice: number;
  DefaultTax: number;
  ImageUrl: string;
  CategoryIDs: string;
  PackageIDs: string;
  RelationIDs: string;
  ParentProductID: number;
}

const TYPE_OPTIONS = [
  { value: 0, label: "Standard" },
  { value: 1, label: "Bundle" },
  { value: 2, label: "Digital" },
  { value: 3, label: "Service" },
  { value: 4, label: "Subscription" },
  { value: 5, label: "Child" },
  { value: 6, label: "Parent" },
];

const PRICE_TYPE_OPTIONS = [
  { value: 0, label: "Standard" },
  { value: 1, label: "Per Unit" },
  { value: 2, label: "Per Weight" },
];

/* ── Component ──────────────────────────────────────────── */

export const EditProductCS = forwardRef<ProductEditorHandle, ProductEditorProps>(
  function EditProductCS({ productId, onSaved, onStateChange }, ref) {
  const [product, setProduct] = useState<CSProduct>(emptyProduct());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [packageInput, setPackageInput] = useState("");
  const [relationInput, setRelationInput] = useState("");

  const isNew = !productId;
  const isChild = product.Type === 5;
  const { upsertProduct } = useAuth();

  useEffect(() => {
    onStateChange?.({ saving, status, dirty });
  }, [saving, status, dirty, onStateChange]);

  useEffect(() => {
    if (productId <= 0) {
      setProduct(emptyProduct());
      setDirty(false);
      return;
    }
    setLoading(true);
    servicePost<CSProduct>("GetProductCS", { id: productId })
      .then(result => {
        if (!result.bError && result.oData) {
          setProduct(result.oData);
          setDirty(false);
        } else {
          setStatus({ type: "error", text: "Failed to load product" });
        }
      })
      .finally(() => setLoading(false));
  }, [productId]);

  const handleChange = useCallback((field: keyof CSProduct, value: string | number | boolean) => {
    setProduct(prev => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!product.UniqueName.trim()) {
      setStatus({ type: "error", text: "Unique name is required." });
      return;
    }
    setSaving(true);
    setStatus(null);
    try {
      const result = await servicePost<CSProduct>("SaveProductCS", { product });
      if (result.bError || !result.oData) {
        setStatus({ type: "error", text: result.sMessage ?? "Failed to save product" });
      } else {
        // Keep the price the user just submitted — the save response must not
        // blank DefaultPrice/DefaultTax (price is stored separately in
        // S_Generic_Price), or a later save would resubmit 0 and wipe it.
        setProduct(prev => ({ ...result.oData!, DefaultPrice: prev.DefaultPrice, DefaultTax: prev.DefaultTax }));
        setDirty(false);
        upsertProduct({
          ProductID: result.oData.ID,
          Name: result.oData.DisplayName || result.oData.UniqueName,
          Deleted: false,
        });
        setStatus({ type: "success", text: isNew ? "Product Created" : "Product Updated" });
        onSaved();
      }
    } finally {
      setSaving(false);
    }
  }, [product, isNew, onSaved, upsertProduct]);

  useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

  const tabs = useMemo(() => {
    const result = [
      {
        key: "product",
        label: "Product",
        children: (
          <ProductTab
            product={product}
            onChange={handleChange}
            isChild={isChild}
          />
        ),
      },
      {
        key: "pictures",
        label: "Pictures",
        children: (
          <PicturesTab product={product} isNew={isNew} />
        ),
      },
    ];
    if (!isChild) {
      result.push({
        key: "categories",
        label: "Categories",
        children: (
          <CategoriesTab
            categoryIDs={product.CategoryIDs}
            categoryInput={categoryInput}
            setCategoryInput={setCategoryInput}
            onAdd={() => {
              if (categoryInput.trim()) {
                const existing = product.CategoryIDs ? product.CategoryIDs.split(",") : [];
                existing.push(categoryInput.trim());
                handleChange("CategoryIDs", existing.join(","));
                setCategoryInput("");
              }
            }}
          />
        ),
      });
    }
    result.push({
      key: "packages",
      label: "Packages",
      children: (
        <PackagesTab
          packageIDs={product.PackageIDs}
          packageInput={packageInput}
          setPackageInput={setPackageInput}
          onAdd={() => {
            if (packageInput.trim()) {
              const existing = product.PackageIDs ? product.PackageIDs.split(",") : [];
              existing.push(packageInput.trim());
              handleChange("PackageIDs", existing.join(","));
              setPackageInput("");
            }
          }}
        />
      ),
    });
    if (!isChild) {
      result.push({
        key: "relations",
        label: "Relations",
        children: (
          <RelationsTab
            relationIDs={product.RelationIDs}
            relationInput={relationInput}
            setRelationInput={setRelationInput}
            onAdd={() => {
              if (relationInput.trim()) {
                const existing = product.RelationIDs ? product.RelationIDs.split(",") : [];
                existing.push(relationInput.trim());
                handleChange("RelationIDs", existing.join(","));
                setRelationInput("");
              }
            }}
          />
        ),
      });
    }
    return result;
  }, [product, handleChange, isChild, isNew, categoryInput, packageInput, relationInput]);

  if (loading) return <p>Loading...</p>;

  return <SimpleTabs tabs={tabs} />;
},
);

/* ── Tab content components ────────────────────────────── */

function ProductTab({ product, onChange, isChild }: {
  product: CSProduct;
  onChange: (field: keyof CSProduct, value: string | number | boolean) => void;
  isChild: boolean;
}) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Identification">
        <div className={styles.fieldRowStacked}>
          <label>Unique Name</label>
          <input className={styles.fieldInput} value={product.UniqueName} onChange={e => onChange("UniqueName", e.target.value)} />
        </div>
        <div className={styles.fieldRowStacked}>
          <label>Display Name</label>
          <input className={styles.fieldInput} value={product.DisplayName} onChange={e => onChange("DisplayName", e.target.value)} />
        </div>
        <div className={styles.fieldRowStacked}>
          <label>Description</label>
          <FwRichTextarea
            value={product.Description}
            onChange={v => onChange("Description", v)}
            rows={4}
          />
          <p className={styles.hint}>Wrap text in &lt;fw&gt;…&lt;/fw&gt; to use fixed-width font for that block (e.g. for aligned nutrition tables).</p>
        </div>
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Street Date</label>
            <input className={styles.fieldInput} type="date" value={product.StreetDate} onChange={e => onChange("StreetDate", e.target.value)} />
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Barcode</label>
            <input className={styles.fieldInput} value={product.Barcode} onChange={e => onChange("Barcode", e.target.value)} />
          </div>
        </div>
      </SimpleFieldset>

      <SimpleFieldset legend="Configuration">
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Type</label>
            <select className={styles.fieldSelect} value={product.Type} onChange={e => onChange("Type", parseInt(e.target.value))}>
              {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Price Type</label>
            <select className={styles.fieldSelect} value={product.PriceType} onChange={e => onChange("PriceType", parseInt(e.target.value))}>
              {PRICE_TYPE_OPTIONS.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Default Price</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.DefaultPrice} onChange={e => onChange("DefaultPrice", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Default Tax</label>
            <input className={styles.fieldInput} type="number" step="0.01" min="0" value={product.DefaultTax} onChange={e => onChange("DefaultTax", Math.max(0, parseFloat(e.target.value) || 0))} />
          </div>
        </div>
        <div style={{ display: "flex", gap: "var(--space-3)" }}>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Depth</label>
            <input className={styles.fieldInput} type="number" value={product.Depth} onChange={e => onChange("Depth", parseInt(e.target.value) || 0)} />
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Deliver Priority</label>
            <input className={styles.fieldInput} type="number" value={product.DeliverPrio} onChange={e => onChange("DeliverPrio", parseInt(e.target.value) || 0)} />
          </div>
          <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
            <label>Deliver Speed</label>
            <input className={styles.fieldInput} type="number" value={product.DeliverSpeed} onChange={e => onChange("DeliverSpeed", parseInt(e.target.value) || 0)} />
          </div>
        </div>
        <label className={styles.checkLabel}>
          <input type="checkbox" checked={product.SingleDeliver} onChange={e => onChange("SingleDeliver", e.target.checked)} />
          Single Deliver
        </label>
      </SimpleFieldset>

      {isChild && (
        <SimpleFieldset legend="Parent">
          <div className={styles.fieldRowStacked}>
            <label>Parent Product ID</label>
            <input className={styles.fieldInput} type="number" value={product.ParentProductID || ""} onChange={e => onChange("ParentProductID", parseInt(e.target.value) || 0)} />
          </div>
        </SimpleFieldset>
      )}
    </div>
  );
}

function PicturesTab({ product, isNew }: { product: CSProduct; isNew: boolean }) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Product Image">
        <div className={styles.fieldRowStacked}>
          <label>Image</label>
          <input className={styles.fieldInput} value={product.ImageUrl} readOnly />
          {product.ImageUrl && (
            <div style={{ textAlign: "center", padding: "var(--space-2)" }}>
              <img src={product.ImageUrl} alt={product.DisplayName} style={{ maxHeight: 200, maxWidth: 300, border: "1px solid var(--color-border-default)" }} />
            </div>
          )}
        </div>
        {isNew && <p className={styles.hint} style={{ color: "var(--color-error)" }}>Create the product before uploading images.</p>}
      </SimpleFieldset>
    </div>
  );
}

function CategoriesTab({ categoryIDs, categoryInput, setCategoryInput, onAdd }: {
  categoryIDs: string;
  categoryInput: string;
  setCategoryInput: (v: string) => void;
  onAdd: () => void;
}) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Product Categories">
        <div className={styles.inlineActions}>
          <input className={styles.fieldInput} value={categoryInput} onChange={e => setCategoryInput(e.target.value)} placeholder="Category ID..." />
          <button className={styles.secondaryBtn} onClick={onAdd}>Add</button>
        </div>
        {categoryIDs && (
          <div style={{ fontSize: "var(--text-xs)", marginTop: "var(--space-1)", color: "var(--color-text-secondary)" }}>
            Assigned: {categoryIDs}
          </div>
        )}
      </SimpleFieldset>
    </div>
  );
}

function PackagesTab({ packageIDs, packageInput, setPackageInput, onAdd }: {
  packageIDs: string;
  packageInput: string;
  setPackageInput: (v: string) => void;
  onAdd: () => void;
}) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Packages">
        <div className={styles.inlineActions}>
          <input className={styles.fieldInput} value={packageInput} onChange={e => setPackageInput(e.target.value)} placeholder="Package ID..." />
          <button className={styles.secondaryBtn} onClick={onAdd}>Add</button>
        </div>
        {packageIDs && (
          <div style={{ fontSize: "var(--text-xs)", marginTop: "var(--space-1)", color: "var(--color-text-secondary)" }}>
            Packages: {packageIDs}
          </div>
        )}
      </SimpleFieldset>
    </div>
  );
}

function RelationsTab({ relationIDs, relationInput, setRelationInput, onAdd }: {
  relationIDs: string;
  relationInput: string;
  setRelationInput: (v: string) => void;
  onAdd: () => void;
}) {
  return (
    <div className={styles.formContainer}>
      <SimpleFieldset legend="Product Relations">
        <div className={styles.inlineActions}>
          <input className={styles.fieldInput} value={relationInput} onChange={e => setRelationInput(e.target.value)} placeholder="Related Product ID..." />
          <button className={styles.secondaryBtn} onClick={onAdd}>Add</button>
        </div>
        {relationIDs && (
          <div style={{ fontSize: "var(--text-xs)", marginTop: "var(--space-1)", color: "var(--color-text-secondary)" }}>
            Relations: {relationIDs}
          </div>
        )}
      </SimpleFieldset>
    </div>
  );
}

/* ── Helpers ───────────────────────────────────────────── */

function emptyProduct(): CSProduct {
  return {
    ID: 0, OperatorID: 0, UniqueName: "", DisplayName: "",
    Description: "", StreetDate: "", Barcode: "", Depth: 0,
    DeliverPrio: 0, SingleDeliver: false, DeliverSpeed: 0,
    Type: 0, PriceType: 0, DefaultPrice: 0, DefaultTax: 0,
    ImageUrl: "", CategoryIDs: "", PackageIDs: "", RelationIDs: "",
    ParentProductID: 0,
  };
}
